import type React from "react";
import { Box, Typography } from "@mui/material";
import { Person } from "@mui/icons-material"; // <-- Import MUI Person icon
import muzukuruLogo from "../../../../assets/thumbnailpurplebackground.jpeg";

interface FuneralPreviewCardProps {
  deceasedName: string;
  birthDate: string;
  deathDate: string;
  serviceDate: string;
  serviceTime: string;
  timeZone: string;
  location: string;
  isLiveStream: boolean;
  broadcastBy: string;
  broadcastByLogo?: string;
  backgroundImage?: string;
  circleImageUrl?: string;
}

const FuneralPreviewCard: React.FC<FuneralPreviewCardProps> = ({
  deceasedName,
  birthDate,
  deathDate,
  serviceDate,
  serviceTime,
  timeZone,
  location,
  isLiveStream,
  broadcastBy,
  broadcastByLogo,
  backgroundImage = muzukuruLogo,
  circleImageUrl = "",
}) => {
  return (
    <Box
      sx={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        borderRadius: "20px",
        overflow: "hidden",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        color: "white",
        width: "100%",
        aspectRatio: "16/9",
        fontFamily: '"Montserrat", sans-serif',
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          width: "100%",
          height: "100%",
          justifyContent: "center",
          alignItems: "center",
          px: 3,
          gap: 2,
        }}
      >
        {/* Left Section */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <Box
            sx={{
              width: "120px",
              height: "120px",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "3px solid rgba(255,255,255,0.7)",
              backgroundColor: "rgba(255,255,255,0.1)",
              mb: 1,
            }}
          >
            <Person sx={{ fontSize: 60, color: "rgba(255,255,255,0.7)" }} />
          </Box>

          <Typography
            sx={{
              fontSize: "8px",
              opacity: 0.9,
              textAlign: "center",
            }}
          >
            {birthDate} – {deathDate}
          </Typography>

          <Box sx={{ textAlign: "center", mt: 1 }}>
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 600,
                textTransform: "uppercase",
                mb: 0.5,
                letterSpacing: "1px",
              }}
            >
              BROADCAST BY
            </Typography>
            {broadcastByLogo ? (
              <Box
                component="img"
                src={broadcastByLogo}
                alt={broadcastBy}
                sx={{ width: 80, objectFit: "contain" }}
              />
            ) : (
              <Typography sx={{ fontSize: "13px", fontWeight: 700 }}>
                {broadcastBy}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Right Section */}
        <Box
          sx={{
            flex: 1.2,
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.8,
          }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              fontStyle: "italic",
              opacity: 0.85,
            }}
          >
            In Loving Memory Of
          </Typography>

          <Typography
            sx={{
              fontSize: "24px",
              fontWeight: 700,
              lineHeight: 1.2,
              textTransform: "capitalize",
              textAlign: "center",
            }}
          >
            {deceasedName}
          </Typography>

          {isLiveStream && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                mt: 0.5,
              }}
            >
              <Typography
                sx={{
                  backgroundColor: "#FF0000",
                  px: 1,
                  py: "2px",
                  fontWeight: 700,
                  borderRadius: "3px",
                  fontSize: "12px",
                }}
              >
                LIVE
              </Typography>
              <Typography sx={{ fontSize: "12px", fontWeight: 600 }}>
                STREAM
              </Typography>
            </Box>
          )}

          <Typography
            sx={{
              fontSize: "13px",
              mt: 0.5,
              fontWeight: 400,
            }}
          >
            {serviceDate}
          </Typography>
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 700,
            }}
          >
            {serviceTime} HRS ({timeZone})
          </Typography>

          <Typography
            sx={{
              fontSize: "10px",
              textTransform: "uppercase",
              letterSpacing: "1px",
              opacity: 0.8,
              mt: 0.8,
            }}
          >
            BROADCASTING LIVE FROM
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 500,
              lineHeight: 1.3,
              textAlign: "center",
            }}
          >
            {location}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default FuneralPreviewCard;
